import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Calendar,
  User,
  Clock,
  ArrowLeft,
} from 'lucide-react';
import { useBlogPost, useBlogPosts } from '@/hooks/useProducts';
import { formatDate } from '@/lib/utils';
import DocumentHead from '@/components/DocumentHead';
import StockPhotoCredit from '@/components/StockPhotoCredit';
import JsonLd from '@/components/JsonLd';
import { SITE_NAME, SITE_URL } from '@/config/site';
import QuickEditWrapper from '@/components/quickedit/QuickEditWrapper';

function RelatedPosts({ currentId, categorySlug }: { currentId: string; categorySlug?: string }) {
  const { t } = useTranslation('blog');
  const { data } = useBlogPosts(1, categorySlug);
  const posts = (data?.posts ?? [])
    .filter((p: any) => p.id !== currentId)
    .slice(0, 3);

  if (posts.length === 0) return null;

  return (
    <section aria-labelledby="related-posts-heading" className="mt-12 border-t border-line pt-12">
      <h2 id="related-posts-heading" className="text-2xl font-bold text-ink">
        {t('post.relatedHeading')}
      </h2>
      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post: any) => (
          <article
            key={post.id}
            className="group overflow-hidden rounded-xl border border-line bg-surface shadow-sm transition-shadow hover:shadow-md"
          >
            <Link
              to={`/blog/${post.slug}`}
              className="block overflow-hidden bg-surface-muted aspect-video focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-inset"
            >
              {post.featured_image_url ? (
                <img
                  src={post.featured_image_url}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Calendar className="h-8 w-8 text-ink-muted" aria-hidden="true" />
                </div>
              )}
            </Link>
            <div className="p-4">
              <h3 className="font-semibold text-ink">
                <Link
                  to={`/blog/${post.slug}`}
                  className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                >
                  {post.title}
                </Link>
              </h3>
              {post.published_at && (
                <time
                  dateTime={post.published_at}
                  className="mt-1 block text-xs text-ink-muted"
                >
                  {formatDate(post.published_at)}
                </time>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function BlogPost() {
  const { t } = useTranslation('blog');
  const { t: tPages } = useTranslation('pages');
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, isError } = useBlogPost(slug || '');

  if (isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8" aria-busy="true">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-48 rounded bg-surface-muted" />
          <div className="h-10 rounded bg-surface-muted w-3/4" />
          <div className="h-4 w-64 rounded bg-surface-muted" />
          <div className="aspect-video rounded-xl bg-surface-muted" />
          <div className="space-y-3">
            <div className="h-4 rounded bg-surface-muted" />
            <div className="h-4 rounded bg-surface-muted" />
            <div className="h-4 rounded bg-surface-muted w-2/3" />
          </div>
        </div>
      </main>
    );
  }

  if (isError || !post) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 text-center">
        <Calendar className="mx-auto h-16 w-16 text-ink-muted" aria-hidden="true" />
        <h1 className="mt-4 text-2xl font-bold text-ink">{t('post.notFound.heading')}</h1>
        <p className="mt-2 text-ink-soft">
          {t('post.notFound.description')}
        </p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-sage-600 px-4 py-2 text-sm font-medium text-white hover:bg-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('post.notFound.back')}
        </Link>
      </main>
    );
  }

  return (
    // i18n-exempt: admin-only QuickEdit label, never customer-visible
    <QuickEditWrapper href={`/admin/blog/${post.id}`} label={`Edit post: ${post.title}`}>
    <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <DocumentHead
        title={post.title}
        description={post.excerpt || post.meta_description}
        ogImage={post.featured_image_url}
        ogType="article"
        canonicalUrl={`/blog/${post.slug}`}
      />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt || post.meta_description,
        image: post.featured_image_url || undefined,
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        author: post.author_name ? {
          '@type': 'Person',
          name: post.author_name,
        } : undefined,
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: SITE_URL,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/blog/${post.slug}`,
        },
      }} />
      {/* Breadcrumbs */}
      <nav aria-label={tPages('breadcrumbs.label')} className="mb-8">
        <ol className="flex items-center gap-1.5 text-sm text-ink-muted">
          <li>
            <Link
              to="/"
              className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
            >
              {tPages('breadcrumbs.home')}
            </Link>
          </li>
          <li aria-hidden="true">
            <ChevronRight className="h-4 w-4" />
          </li>
          <li>
            <Link
              to="/blog"
              className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
            >
              {t('title')}
            </Link>
          </li>
          <li aria-hidden="true">
            <ChevronRight className="h-4 w-4" />
          </li>
          <li>
            <span className="font-medium text-ink" aria-current="page">
              {post.title}
            </span>
          </li>
        </ol>
      </nav>

      {/* Article header */}
      <header>
        {post.category_name && (
          <span className="text-xs font-semibold uppercase tracking-wider text-sage-700">
            {post.category_name}
          </span>
        )}
        <h1 className="mt-2 text-3xl font-bold text-ink sm:text-4xl">
          {post.title}
        </h1>

        {/* Author and date */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-ink-muted">
          {post.author_name && (
            <span className="flex items-center gap-1.5">
              {post.author_avatar ? (
                <img
                  src={post.author_avatar}
                  alt=""
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <User className="h-4 w-4" aria-hidden="true" />
              )}
              <span>{post.author_name}</span>
            </span>
          )}
          {post.published_at && (
            <time dateTime={post.published_at} className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" aria-hidden="true" />
              {formatDate(post.published_at)}
            </time>
          )}
          {post.reading_time && (
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" aria-hidden="true" />
              {t('post.readTime', { minutes: post.reading_time })}
            </span>
          )}
        </div>
      </header>

      {/* Featured image */}
      {post.featured_image_url && (
        <figure className="mt-8">
          <div className="overflow-hidden rounded-xl">
            <img
              src={post.featured_image_url}
              alt=""
              className="w-full object-cover"
            />
          </div>
          <StockPhotoCredit attribution={post.image_attribution} />
        </figure>
      )}

      {/* Article content */}
      <article className="mt-8">
        <div
          className="prose prose-stone dark:prose-invert max-w-none prose-headings:font-semibold prose-a:text-sage-700 prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: (post.content || '').replace(/^\s*<h1[^>]*>.*?<\/h1>\s*/i, '') }}
        />
      </article>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2" aria-label={t('post.tagsAriaLabel')}>
          {post.tags.map((tag: string) => (
            <span
              key={tag}
              className="rounded-full bg-surface-muted px-3 py-1 text-xs font-medium text-ink-soft"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Back to blog */}
      <div className="mt-8 border-t border-line pt-6">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm font-medium text-sage-700 hover:text-sage-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('post.backToAll')}
        </Link>
      </div>

      {/* Related posts */}
      <RelatedPosts
        currentId={post.id}
        categorySlug={post.category_slug}
      />
    </main>
    </QuickEditWrapper>
  );
}
